import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ComponentType } from "react";
import { useAdapter, useTopology } from "@/store/site";
import { cn } from "@/lib/cn";
import { useT } from "@/lib/i18n";
import { LOCALE, formatHourMin } from "@/lib/format";
import type { BessEnergySample, EnergyGran } from "@/data/adapter";

/**
 * Shared bucket-browser machinery for the BESS energy charts.
 *
 * Both the throughput chart (`BessEnergyBrowser`, on the BESS page) and
 * the discharge-value chart (`BessSavingsBrowser`, on the Revenue page)
 * slice the same `EP_<g>` / `EN_<g>` per-bucket kWh timeseries the same
 * two ways — granularity (15m … year) and BESS scope — and apply the
 * same capacity-cap sanity filter. Keeping that here means the two
 * charts can never disagree on the underlying kWh numbers.
 */

/**
 * Granularities where the per-bucket capacity-cap sanity filter applies.
 * Short buckets (15m / h / d) can legitimately exceed nominal capacity
 * via multiple intra-bucket cycles; week / month / year buckets cannot.
 */
export const CAP_APPLIES: ReadonlySet<EnergyGran> = new Set(["w", "m", "y"]);

/**
 * Bar width in pixels per granularity. Chosen so bars stay readable at
 * each default window:
 *   15m → 6 (24 bars in a 6 h window)
 *   h   → 8 (168 bars in a 7 d window)
 *   d   → 14 (30 bars in 30 d)
 *   w   → 18 (~26 bars in 6 mo) — explicit fix for the "pencil thin"
 *         render where ECharts collapsed bar width to the smallest
 *         observed time gap.
 *   m   → 20 (24 bars in 24 mo)
 *   y   → 28 (5 bars in 5 yr)
 */
export const BAR_WIDTH_BY_GRAN: Record<EnergyGran, number> = {
  "15m": 6,
  h:     8,
  d:     14,
  w:     18,
  m:     20,
  y:     28,
};

export const GRAN_CHIPS: Array<{ key: EnergyGran; label: string }> = [
  { key: "15m", label: "15 min" },
  { key: "h",   label: "1 hour" },
  { key: "d",   label: "1 day"  },
  { key: "w",   label: "1 week" },
  { key: "m",   label: "1 month" },
  { key: "y",   label: "1 year" },
];

/** Sensible default window per granularity (so bars are readable). */
export const WINDOW_PER_GRAN: Record<EnergyGran, { ms: number; label: string }> = {
  "15m": { ms: 6  * 3_600_000,             label: "last 6 h"     },
  "h":   { ms: 7  * 24 * 3_600_000,        label: "last 7 d"     },
  "d":   { ms: 30 * 24 * 3_600_000,        label: "last 30 d"    },
  "w":   { ms: 26 * 7  * 24 * 3_600_000,   label: "last 6 mo"    },
  "m":   { ms: 24 * 30 * 24 * 3_600_000,   label: "last 24 mo"   },
  "y":   { ms: 5  * 365 * 24 * 3_600_000,  label: "last 5 yr"    },
};

/**
 * Nominal bucket span per granularity (ms). w/m/y are approximations —
 * used ONLY as the single-bucket fallback in partialBucketTs (where there
 * is no neighbouring bucket to derive the real spacing from). Day-and-finer
 * are exact.
 */
export const GRAN_NOMINAL_SPAN_MS: Record<EnergyGran, number> = {
  "15m": 15 * 60_000,
  h:     3_600_000,
  d:     86_400_000,
  w:     7 * 86_400_000,
  m:     30 * 86_400_000,
  y:     365 * 86_400_000,
};

/**
 * The bucket start ts of the trailing bucket when it is STILL FILLING (the
 * current wall-clock falls inside it), else null. Because every auto window
 * ends at `Date.now()`, the last bucket of a now-ending window is partial —
 * today's day bar, this week's week bar — yet it's drawn the same height as
 * a complete bucket, so a half-finished bar reads as a collapse. Charts use
 * this to fade the partial bar + flag it "(in progress)" — never lie about
 * an incomplete bucket (ported concept: TB-EMS ems-dashboard partial-bucket
 * marker).
 *
 * The span is derived from the spacing between the last two buckets so it's
 * robust to UTC-vs-local bucket alignment and to month/year irregularity;
 * the nominal span is only the single-bucket fallback. A stepped PAST window
 * (Revenue ‹) yields a last bucket already complete (now ≫ end) → null, so
 * historical windows are never falsely annotated.
 */
export function partialBucketTs(
  samples: { ts: number }[],
  gran: EnergyGran,
  now: number,
): number | null {
  if (samples.length === 0) return null;
  const lastTs = samples[samples.length - 1].ts;
  const span =
    samples.length >= 2
      ? lastTs - samples[samples.length - 2].ts
      : GRAN_NOMINAL_SPAN_MS[gran];
  return span > 0 && now >= lastTs && now < lastTs + span ? lastTs : null;
}

/** Opacity of the trailing in-progress bucket's bar (partial-bucket marker). */
export const PARTIAL_OPACITY = 0.4;

/**
 * A bar datum is either a plain `[ts, value]` tuple, or the object form
 * ECharts accepts for per-point styling (`{ value: [ts, value], itemStyle }`).
 * The trailing in-progress bucket uses the object form so it can be faded;
 * every other bucket stays a tuple.
 */
export type BarDatum = [number, number] | { value: [number, number]; itemStyle?: object };

/**
 * Read the y-value (index 1) from a bar datum in either form — tooltip
 * formatters must NOT index `data[1]` directly, because the object-form
 * partial bucket would yield `undefined` (silently read as 0, a lie about
 * the in-progress bar). Returns 0 only when the datum is genuinely absent.
 */
export function barDatumValue(d: BarDatum | undefined | null): number {
  const arr = Array.isArray(d) ? d : d && Array.isArray(d.value) ? d.value : null;
  return typeof arr?.[1] === "number" ? arr[1] : 0;
}

/**
 * Granularities where a half-filled trailing bar would be misread as a drop,
 * so the explanatory caption earns its line. Finer grans (15m/h) still fade
 * the bar + flag it in the tooltip, but skip the always-visible caption.
 */
export const NOTE_GRANS: ReadonlySet<EnergyGran> = new Set(["d", "w", "m", "y"]);

/**
 * ECharts markArea `data` pairs (one per weekend run in the window) for a faint
 * Sat–Sun backdrop behind the DAILY bars, so weekday/weekend dispatch patterns
 * read at a glance. ONLY meaningful at daily granularity — weekly+ buckets each
 * span a weekend, and 15m/h are far too fine — so returns [] for every other
 * gran.
 *
 * Weekend detection uses LOCAL calendar days (`Date.getDay()`), matching the
 * local-time axis labels (bucketAxisLabel → toLocaleDateString). Band endpoints
 * are derived from the actual weekend bucket timestamps (± half a nominal day
 * so the band sits centred on the weekend bars), NOT from `ts + 2·day` math —
 * the half-day pad on a faint backdrop is DST-immune, and a run that touches a
 * month/year boundary is handled because it keys off real sample ts. Contiguous
 * Sat+Sun collapse into one band; a window edge that lands mid-weekend (lone
 * leading Sun / trailing Sat) still shades correctly.
 */
export function weekendMarkAreaData(
  samples: { ts: number }[],
  gran: EnergyGran,
): Array<[{ xAxis: number }, { xAxis: number }]> {
  if (gran !== "d" || samples.length < 2) return [];
  const half = GRAN_NOMINAL_SPAN_MS.d / 2;
  const out: Array<[{ xAxis: number }, { xAxis: number }]> = [];
  let runStart: number | null = null;
  let runLast: number | null = null;
  const flush = () => {
    if (runStart !== null && runLast !== null) {
      out.push([{ xAxis: runStart - half }, { xAxis: runLast + half }]);
    }
    runStart = null;
    runLast = null;
  };
  for (const s of samples) {
    const day = new Date(s.ts).getDay(); // 0=Sun … 6=Sat, LOCAL
    if (day === 0 || day === 6) {
      if (runStart === null) runStart = s.ts;
      runLast = s.ts;
    } else {
      flush();
    }
  }
  flush(); // trailing weekend run, if the window ends on Sat/Sun
  return out;
}

/* -------------------------------------------------------------------------- */
/* Data hook — query + scope aggregation + capacity-cap filter                 */
/* -------------------------------------------------------------------------- */

export interface BessEnergyBuckets {
  /** Buckets summed across the selected BESS scope, capacity-cap filtered. */
  aggregated: BessEnergySample[];
  /** Total nominal capacity of the selected scope (drives cycle KPIs). */
  totalCap: number;
  isLoading: boolean;
  isError: boolean;
  /** The auto-snapped window for this granularity (for loading captions). */
  window: { ms: number; label: string };
}

/**
 * Fetches the per-BESS `EP_<g>`/`EN_<g>` buckets for the selected scope
 * and granularity, sums them into one flat list aligned by bucket ts,
 * and applies the capacity-cap sanity filter for week/month/year buckets.
 *
 * Sanity filter: EMS-server aggregates over long windows can drift when
 * a device misses an uplink or a cumulative counter rolls — producing
 * buckets whose NET energy exceeds the physical fleet capacity (which is
 * impossible). Spot those buckets and rebase BOTH halves to the larger
 * value: the chart looks balanced, the throughput stays honest, and the
 * operator isn't shown obviously-wrong data. The behaviour is
 * INTENTIONALLY silent — no badge or warning — per product requirement.
 */
export function useBessEnergyBuckets(gran: EnergyGran, scope: string[]): BessEnergyBuckets {
  const adapter = useAdapter();
  const topology = useTopology();

  const window = WINDOW_PER_GRAN[gran];
  const range = useMemo(() => {
    const end = Date.now();
    return { start: end - window.ms, end };
  }, [window, gran]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["bess-energy-history", topology.id, gran, scope.join(","), range.start, range.end],
    queryFn: () => adapter.bessEnergyHistory({
      bessIds: scope.length ? scope : undefined,
      granularity: gran,
      start_ts: range.start,
      end_ts: range.end,
    }),
    staleTime: 30_000,
  });

  const totalCap = useMemo(
    () => scope.reduce(
      (s, id) => s + (topology.bess.find((b) => b.id === id)?.capacity_kWh ?? 0),
      0,
    ),
    [scope, topology.bess],
  );

  const aggregated = useMemo<BessEnergySample[]>(() => {
    if (!data) return [];
    const byTs = new Map<number, BessEnergySample>();
    for (const bessId of scope) {
      const series = data.perBess[bessId] ?? [];
      for (const s of series) {
        const cur = byTs.get(s.ts) ?? { ts: s.ts, charged_kWh: 0, discharged_kWh: 0 };
        cur.charged_kWh += s.charged_kWh;
        cur.discharged_kWh += s.discharged_kWh;
        byTs.set(s.ts, cur);
      }
    }
    const raw = [...byTs.values()].sort((a, b) => a.ts - b.ts);

    // 15-min / hour / day buckets are NEVER capped: at those granularities
    // a real charge-discharge cycle within one bucket can legitimately
    // exceed nominal capacity (e.g. an 800 kWh BESS doing two cycles in
    // a day → 1600 kWh throughput, which is correct).
    if (!CAP_APPLIES.has(gran) || totalCap <= 0) return raw;
    return raw.map((s) => {
      const net = Math.abs(s.charged_kWh - s.discharged_kWh);
      if (net > totalCap) {
        const peak = Math.max(s.charged_kWh, s.discharged_kWh);
        return { ts: s.ts, charged_kWh: peak, discharged_kWh: peak };
      }
      return s;
    });
  }, [data, scope, gran, totalCap]);

  return { aggregated, totalCap, isLoading, isError, window };
}

/* -------------------------------------------------------------------------- */
/* Controls + tile helpers                                                     */
/* -------------------------------------------------------------------------- */

export function GranChips({ value, onChange }: { value: EnergyGran; onChange: (g: EnergyGran) => void }) {
  const t = useT();
  return (
    <div className="flex items-center gap-0.5 rounded-md border border-edge bg-surface p-0.5">
      <span className="px-1.5 text-[10px] uppercase tracking-wider text-text-faint">{t("analyze.chips.bucket")}</span>
      {GRAN_CHIPS.map((g) => (
        <button
          key={g.key}
          onClick={() => onChange(g.key)}
          className={cn(
            "rounded px-2 py-1 text-[11px] font-medium text-text-muted transition hover:text-text",
            value === g.key && "bg-accent-soft text-accent-strong",
          )}
        >
          {/* EnergyGran slugs match the analyze.granLabel.* dict keys, so
              the English GRAN_CHIPS labels are translated at render. */}
          {t(`analyze.granLabel.${g.key}`)}
        </button>
      ))}
    </div>
  );
}

export function ScopeChips({
  topology, scope, onChange,
}: {
  topology: { id: string; name: string }[];
  scope: string[];
  onChange: (next: string[]) => void;
}) {
  const t = useT();
  const allOn = topology.every((b) => scope.includes(b.id));
  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-edge bg-surface-2/40 px-4 py-2">
      <span className="text-[10px] uppercase tracking-wider text-text-faint">{t("analyze.chips.scope")}</span>
      <button
        onClick={() => onChange(allOn ? [] : topology.map((b) => b.id))}
        className={cn(
          "rounded-md border px-2 py-1 text-[11px] font-medium",
          allOn ? "border-accent bg-accent/15 text-accent" : "border-edge text-text-muted hover:border-text-muted",
        )}
      >
        {t("analyze.chips.fleet", { n: topology.length })}
      </button>
      {topology.map((b) => {
        const on = scope.includes(b.id);
        return (
          <button
            key={b.id}
            onClick={() => onChange(on ? scope.filter((x) => x !== b.id) : [...scope, b.id])}
            className={cn(
              "rounded-md border px-2 py-1 text-[11px] font-medium",
              on ? "border-accent bg-accent/15 text-accent" : "border-edge text-text-muted hover:border-text-muted",
            )}
          >
            {b.name}
          </button>
        );
      })}
    </div>
  );
}

export function Kpi({
  icon: Icon, tone, label, value, unit, sub,
}: {
  // Lucide ForwardRef component — type relaxed for compatibility.
  icon: ComponentType<{ size?: string | number; className?: string }>;
  tone?: "charge" | "discharge" | "export";
  label: string;
  value: string;
  unit?: string;
  sub?: string;
}) {
  const colorCls = tone === "charge"    ? "text-charge"
    : tone === "discharge" ? "text-discharge"
    : tone === "export"    ? "text-export"
    : "text-text";
  return (
    <div className="rounded-md border border-edge bg-surface p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-text-faint font-semibold">
        <Icon size={11} /> {label}
      </div>
      <div className={cn("mt-1 text-xl font-bold num leading-none", colorCls)}>
        {value}{unit && <span className="ml-1 text-xs font-medium text-text-muted">{unit}</span>}
      </div>
      {sub && <div className="mt-0.5 text-[10px] text-text-muted">{sub}</div>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Bucket axis + tooltip formatters                                           */
/* -------------------------------------------------------------------------- */

// All labels are pinned to the app-wide LOCALE / format.ts helpers
// (DSGN-11) — previously `[]` floated with the browser locale, splitting
// date shapes between this chart and the rest of the SPA.
export function bucketAxisLabel(g: EnergyGran): (ts: number) => string {
  return (ts) => {
    const d = new Date(ts);
    switch (g) {
      case "15m":
      case "h":
        return formatHourMin(ts);
      case "d":
        return d.toLocaleDateString(LOCALE, { weekday: "short", day: "2-digit" });
      case "w":
        return `${d.toLocaleDateString(LOCALE, { month: "short", day: "2-digit" })}`;
      case "m":
        return d.toLocaleDateString(LOCALE, { month: "short", year: "2-digit" });
      case "y":
        return d.getFullYear().toString();
    }
  };
}

export function bucketTooltipLabel(ts: number, g: EnergyGran): string {
  const d = new Date(ts);
  switch (g) {
    case "15m":
      return `${d.toLocaleString(LOCALE, { dateStyle: "medium", timeStyle: "short" })} (15 min bucket)`;
    case "h":
      return `${d.toLocaleString(LOCALE, { dateStyle: "medium", timeStyle: "short" })} (hour bucket)`;
    case "d":
      return d.toLocaleDateString(LOCALE, { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
    case "w":
      return `Week of ${d.toLocaleDateString(LOCALE, { day: "2-digit", month: "short", year: "numeric" })}`;
    case "m":
      return d.toLocaleDateString(LOCALE, { month: "long", year: "numeric" });
    case "y":
      return d.getFullYear().toString();
  }
}
